import React, { useMemo, useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { userService } from '../services/api/users';
import { organisationService } from '../services/api/organisations';
import { perimeterService } from '../services/api/perimeters';
import { queryKeys } from '../lib/react-query';
import { User, Organisation, UserRole, Perimeter } from '../types/api';
import { useCurrentUser } from '../hooks/useCurrentUser';
import { Card, CardContent } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { ChangePasswordModal } from '../components/ChangePasswordModal';

type EditableUser = User & {
  _dirty?: boolean;
  _editing?: boolean;
};

const roleOptions: UserRole[] = ['ADMIN', 'USER'];

export function Users() {
  const qc = useQueryClient();
  const { user: me } = useCurrentUser();

  const isAdmin = useMemo(
    () => me?.role === 'ADMIN' || me?.role === 'SUPER_ADMIN',
    [me?.role],
  );

  // Load users
  const { data: users = [], isLoading: usersLoading } = useQuery({
    queryKey: ['users', 'list'],
    queryFn: () => userService.getUsers(),
    staleTime: 60_000,
  });

  // Load perimeters for assignment
  const { data: perimeters = [], isLoading: perimetersLoading } = useQuery({
    queryKey: queryKeys.perimeters.lists(),
    queryFn: () => perimeterService.getPerimeters(),
    staleTime: 5 * 60_000,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
  });

  // Load organisations for assignment
  const { data: organisations = [], isLoading: orgsLoading } = useQuery({
    queryKey: queryKeys.organisations.lists(),
    queryFn: () => organisationService.getOrganisations(),
    staleTime: 5 * 60_000,
  });

  const [edits, setEdits] = useState<Record<number, EditableUser>>({});
  const [passwordChangeUser, setPasswordChangeUser] = useState<User | null>(null);

  // Création d'utilisateur (ADMIN / SUPER_ADMIN)
  type NewUser = {
    fullName: string;
    email: string;
    role: UserRole;
    perimeterId: number | null;
    organisationId: number | null;
    password: string;
  };

  const [newUser, setNewUser] = useState<NewUser>({
    fullName: '',
    email: '',
    role: 'USER',
    perimeterId: me?.perimeterId ?? null,
    organisationId: null,
    password: '',
  });

  // Auto-remplir le périmètre pour les ADMIN
  useEffect(() => {
    if (me?.role === 'ADMIN' && me.perimeterId) {
      setNewUser(prev => ({
        ...prev,
        perimeterId: me.perimeterId
      }));
    }
  }, [me, me?.perimeterId]);

  const createMutation = useMutation({
    mutationFn: (payload: { fullName: string; email: string; role: UserRole; perimeterId: number | null; organisationId: number | null; password: string; }) =>
      userService.createUser(payload),
    onSuccess: async () => {
      setNewUser({ fullName: '', email: '', role: 'USER', perimeterId: me?.perimeterId ?? null, organisationId: null, password: '' });
      await qc.invalidateQueries({ queryKey: ['users', 'list'] });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, updates }: { id: number; updates: Partial<User> }) =>
      userService.updateUser(id, {
        fullName: updates.fullName,
        email: updates.email,
        role: updates.role,
        perimeterId: updates.perimeterId,
        organisationId: updates.organisationId,
        isActive: updates.isActive,
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['users', 'list'] });
    },
  });

  const onFieldChange = (u: User, field: keyof EditableUser, value: any) => {
    setEdits(prev => {
      const base = (prev[u.id] ?? ({ ...u, _editing: true } as EditableUser));
      return {
        ...prev,
        [u.id]: {
          ...base,
          [field]: value,
          _dirty: true,
        },
      };
    });
  };

  const onStartEdit = (u: User) => {
    setEdits(prev => ({
      ...prev,
      [u.id]: {
        ...(prev[u.id] ?? u),
        _editing: true,
        _dirty: false,
      },
    }));
  };

  const onCancelEdit = (u: User) => {
    setEdits(prev => {
      const cp = { ...prev };
      delete cp[u.id];
      return cp;
    });
  };

  const onSave = async (u: User) => {
    const draft = edits[u.id];
    if (!draft || !draft._dirty) return;
    await updateMutation.mutateAsync({
      id: u.id,
      updates: {
        fullName: draft.fullName,
        email: draft.email,
        role: draft.role,
        perimeterId:
          draft.perimeterId === undefined
            ? u.perimeterId
            : (draft.perimeterId as number | null),
        organisationId:
          draft.organisationId === undefined
            ? u.organisationId
            : (draft.organisationId as number | null),
        isActive: draft.isActive,
      },
    });
    setEdits(prev => {
      const cp = { ...prev };
      delete cp[u.id];
      return cp;
    });
  };

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-gray-50 px-4 py-4 sm:px-6 sm:py-6">
        <div className="max-w-6xl mx-auto">
          <Card title="Accès refusé" variant="shadow">
            <CardContent>
              <div className="text-center py-10">
                <div className="text-2xl font-bold text-gray-900 mb-2">403</div>
                <div className="text-gray-600">
                  Vous n'avez pas les permissions pour accéder à la gestion des utilisateurs.
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const sortedUsers = useMemo(
    () =>
      [...users].sort((a, b) => {
        // Current user first
        if (me?.id === a.id) return -1;
        if (me?.id === b.id) return 1;
        // Then admins
        const rank = (r: UserRole) =>
          r === 'SUPER_ADMIN' ? 3 : r === 'ADMIN' ? 2 : r === 'USER' ? 1 : 0;
        return rank(b.role) - rank(a.role);
      }),
    [users, me?.id],
  );

  return (
    <div className="min-h-screen bg-gray-50 px-4 py-4 sm:px-6 sm:py-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Gestion des utilisateurs</h1>
            <p className="text-sm text-gray-600">Créer, modifier les rôles et activer/désactiver les comptes</p>
          </div>
        </div>

        {isAdmin && (
          <Card variant="shadow" title="Créer un utilisateur">
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Nom complet"
                  placeholder="Ex: Fatou Diop"
                  value={newUser.fullName}
                  onChange={(e) => setNewUser((p) => ({ ...p, fullName: e.target.value }))}
                />
                <Input
                  label="Email"
                  placeholder="exemple@domaine.tld"
                  value={newUser.email}
                  onChange={(e) => setNewUser((p) => ({ ...p, email: e.target.value }))}
                />

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Périmètre
                    {me?.role === 'ADMIN' && (
                      <span className="text-gray-500 text-xs ml-1">(fixé à votre périmètre)</span>
                    )}
                  </label>
                  {me?.role === 'ADMIN' ? (
                    <div className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-gray-100">
                      {perimeters.find(p => p.id === newUser.perimeterId)?.displayName || 'Chargement...'}
                    </div>
                  ) : (
                    <select
                      value={newUser.perimeterId ?? ''}
                      onChange={(e) =>
                        setNewUser((p) => ({
                          ...p,
                          perimeterId: e.target.value === '' ? null : Number(e.target.value),
                        }))
                      }
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 h-10"
                    >
                      <option value="">— Sélectionner un périmètre —</option>
                      {perimeters.map((p: Perimeter) => (
                        <option key={p.id} value={p.id}>{p.displayName}</option>
                      ))}
                    </select>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Rôle</label>
                  <select
                    value={newUser.role}
                    onChange={(e) => setNewUser((p) => ({ ...p, role: e.target.value as UserRole }))}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 h-10"
                  >
                    {roleOptions.map((r) => (
                      <option key={r} value={r}>{r}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Organisation</label>
                  <select
                    value={newUser.organisationId ?? ''}
                    onChange={(e) =>
                      setNewUser((p) => ({
                        ...p,
                        organisationId: e.target.value === '' ? null : Number(e.target.value),
                      }))
                    }
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 h-10"
                  >
                    <option value="">— Aucune —</option>
                    {organisations.map((org: Organisation) => (
                      <option key={org.id} value={org.id}>{org.displayName}</option>
                    ))}
                  </select>
                </div>

                <Input
                  label="Mot de passe initial"
                  type="password"
                  placeholder="Au moins 6 caractères"
                  value={newUser.password}
                  onChange={(e) => setNewUser((p) => ({ ...p, password: e.target.value }))}
                />

                <div className="md:col-span-2 flex gap-2 justify-end">
                  <Button
                    variant="outline"
                    onClick={() => setNewUser({ fullName: '', email: '', role: 'USER', perimeterId: me?.perimeterId ?? null, organisationId: null, password: '' })}
                    className="w-full sm:w-auto"
                  >
                    Réinitialiser
                  </Button>
                  <Button
                    onClick={() => createMutation.mutate(newUser)}
                    disabled={createMutation.isPending || !newUser.fullName || !newUser.email || !newUser.password || !newUser.perimeterId}
                    className="w-full sm:w-auto"
                  >
                    {createMutation.isPending ? 'Création...' : 'Créer'}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Users List */}
        <Card variant="shadow" title="Utilisateurs">
          <CardContent>
            {usersLoading ? (
              <div className="text-center py-8">Chargement...</div>
            ) : sortedUsers.length === 0 ? (
              <div className="text-center py-8 text-gray-500">Aucun utilisateur trouvé</div>
            ) : (
              <div className="space-y-4">
                {sortedUsers.map((u) => {
                  const draft = edits[u.id];
                  const isEditing = draft?._editing;

                  return (
                    <div key={u.id} className="p-4 border border-gray-200 rounded-lg">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Nom</label>
                          {isEditing ? (
                            <Input
                              value={draft?.fullName ?? u.fullName}
                              onChange={(e) => onFieldChange(u, 'fullName', e.target.value)}
                            />
                          ) : (
                            <div className="text-sm text-gray-900">{u.fullName}</div>
                          )}
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                          {isEditing ? (
                            <Input
                              value={draft?.email ?? u.email}
                              onChange={(e) => onFieldChange(u, 'email', e.target.value)}
                            />
                          ) : (
                            <div className="text-sm text-gray-900">{u.email}</div>
                          )}
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Périmètre</label>
                          {isEditing ? (
                            <select
                              value={draft?.perimeterId ?? u.perimeterId ?? ''}
                              onChange={(e) =>
                                onFieldChange(u, 'perimeterId', e.target.value === '' ? null : Number(e.target.value))
                              }
                              disabled={me?.role === 'ADMIN'}
                              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 h-10 disabled:bg-gray-100 disabled:cursor-not-allowed"
                            >
                              <option value="">— Aucun —</option>
                              {perimeters.map((p: Perimeter) => (
                                <option key={p.id} value={p.id}>{p.displayName}</option>
                              ))}
                            </select>
                          ) : (
                            <div className="text-sm text-gray-900">
                              {u.perimeterId ? perimeters.find(p => p.id === u.perimeterId)?.displayName : '—'}
                            </div>
                          )}
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Rôle</label>
                          {isEditing ? (
                            <select
                              value={draft?.role ?? u.role}
                              onChange={(e) => onFieldChange(u, 'role', e.target.value as UserRole)}
                              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 h-10"
                            >
                              {roleOptions.map((r) => (
                                <option key={r} value={r}>{r}</option>
                              ))}
                            </select>
                          ) : (
                            <div className="text-sm text-gray-900">{u.role}</div>
                          )}
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Organisation</label>
                          {isEditing ? (
                            <select
                              value={draft?.organisationId ?? u.organisationId ?? ''}
                              onChange={(e) =>
                                onFieldChange(u, 'organisationId', e.target.value === '' ? null : Number(e.target.value))
                              }
                              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 h-10"
                            >
                              <option value="">— Aucune —</option>
                              {organisations.map((org: Organisation) => (
                                <option key={org.id} value={org.id}>{org.displayName}</option>
                              ))}
                            </select>
                          ) : (
                            <div className="text-sm text-gray-900">
                              {u.organisationId ? organisations.find(o => o.id === u.organisationId)?.displayName : '—'}
                            </div>
                          )}
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Statut</label>
                          {isEditing ? (
                            <select
                              value={draft?.isActive ?? u.isActive ? 'active' : 'inactive'}
                              onChange={(e) => onFieldChange(u, 'isActive', e.target.value === 'active')}
                              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 h-10"
                            >
                              <option value="active">Actif</option>
                              <option value="inactive">Inactif</option>
                            </select>
                          ) : (
                            <div className={`text-sm font-medium ${u.isActive ? 'text-green-600' : 'text-red-600'}`}>
                              {u.isActive ? 'Actif' : 'Inactif'}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="mt-4 flex gap-2 justify-end">
                        {isEditing ? (
                          <>
                            <Button
                              variant="outline"
                              onClick={() => onCancelEdit(u)}
                              className="w-full sm:w-auto"
                            >
                              Annuler
                            </Button>
                            <Button
                              onClick={() => onSave(u)}
                              disabled={updateMutation.isPending}
                              className="w-full sm:w-auto"
                            >
                              {updateMutation.isPending ? 'Sauvegarde...' : 'Sauvegarder'}
                            </Button>
                          </>
                        ) : (
                          <>
                            <Button
                              variant="outline"
                              onClick={() => setPasswordChangeUser(u)}
                              className="w-full sm:w-auto"
                            >
                              Changer mot de passe
                            </Button>
                            <Button
                              onClick={() => onStartEdit(u)}
                              className="w-full sm:w-auto"
                            >
                              Modifier
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {passwordChangeUser && (
        <ChangePasswordModal
          user={passwordChangeUser}
          isOpen={true}
          onClose={() => setPasswordChangeUser(null)}
        />
      )}
    </div>
  );
}
